import { createSlice } from '@reduxjs/toolkit';

/**
 * État initial du slice location
 * Gère la localisation de l'utilisateur (GPS ou IP temporaire)
 */
const initialState = {
  // Données de localisation (format GeoJSON)
  location: {
    type: 'Point',
    coordinates: [], // [longitude, latitude]
    geoHash: ''
  },

  // Stratégie de localisation utilisée
  strategy: {
    mode: null, // 'gps' | 'ip_temporary' | null
    isTemporary: false, // true si IP, false si GPS
    lastUpdate: null, // Timestamp de la dernière mise à jour
    accuracy: null, // Précision en mètres
    source: '' // Description de la source (ex: "GPS haute précision", "Abidjan, CI")
  },

  // État de la permission GPS
  gpsPermission: {
    status: 'unknown', // 'unknown' | 'granted' | 'denied' | 'prompt'
    lastAskedAt: null, // Timestamp de la dernière demande GPS
    retryCount: 0, // Nombre de tentatives GPS
    nextRetryAt: null // Timestamp du prochain retry automatique (toutes les 5 min)
  },

  // UI State
  showGpsBanner: false, // Afficher le banner "Activez votre GPS"
  loading: false,
  error: null
};

/**
 * Slice Redux pour la gestion de la localisation
 */
const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    /**
     * Mettre à jour la localisation avec GPS (source prioritaire)
     */
    updateLocationWithGPS: (state, action) => {
      const { coordinates, accuracy } = action.payload;

      state.location.coordinates = coordinates;
      // Le geoHash sera calculé dans le hook
      state.strategy = {
        mode: 'gps',
        isTemporary: false,
        lastUpdate: Date.now(),
        accuracy,
        source: 'GPS haute précision'
      };
      state.gpsPermission.status = 'granted';
      state.showGpsBanner = false; // Cacher le banner si GPS activé
      state.loading = false;
      state.error = null;
    },

    /**
     * Mettre à jour la localisation avec IP (temporaire)
     */
    updateLocationWithIP: (state, action) => {
      const { coordinates, city, country } = action.payload;

      state.location.coordinates = coordinates;
      state.strategy = {
        mode: 'ip_temporary',
        isTemporary: true,
        lastUpdate: Date.now(),
        accuracy: 10000, // ~10km
        source: `${city}, ${country}`
      };
      // Afficher le banner pour inciter à activer GPS
      state.showGpsBanner = true;
      state.loading = false;
      state.error = null;
    },

    /**
     * Mettre à jour le geoHash (appelé après calcul)
     */
    setGeoHash: (state, action) => {
      state.location.geoHash = action.payload;
    },

    /**
     * Mettre à jour l'état de la permission GPS
     */
    setGpsPermission: (state, action) => {
      state.gpsPermission = {
        ...state.gpsPermission,
        ...action.payload,
        lastAskedAt: Date.now()
      };
    },

    /**
     * Incrémenter le compteur de retry GPS
     */
    incrementGpsRetryCount: (state) => {
      state.gpsPermission.retryCount += 1;
    },

    /**
     * Réinitialiser le compteur de retry GPS
     */
    resetGpsRetryCount: (state) => {
      state.gpsPermission.retryCount = 0;
      state.gpsPermission.nextRetryAt = null;
    },

    /**
     * Programmer le prochain retry GPS (5 minutes)
     */
    scheduleGpsRetry: (state) => {
      const RETRY_INTERVAL = 5 * 60 * 1000; // 5 minutes
      state.gpsPermission.nextRetryAt = Date.now() + RETRY_INTERVAL;
    },

    /**
     * Afficher/cacher le banner GPS
     */
    setShowGpsBanner: (state, action) => {
      state.showGpsBanner = action.payload;
    },

    /**
     * Définir l'état de chargement
     */
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    /**
     * Définir une erreur
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    /**
     * Effacer la localisation
     */
    clearLocation: (state) => {
      state.location = {
        type: 'Point',
        coordinates: [],
        geoHash: ''
      };
      state.strategy = {
        mode: null,
        isTemporary: false,
        lastUpdate: null,
        accuracy: null,
        source: ''
      };
      state.showGpsBanner = false;
    },

    /**
     * Réinitialiser complètement l'état
     */
    resetLocationState: () => initialState
  }
});

// Exporter les actions
export const {
  updateLocationWithGPS,
  updateLocationWithIP,
  setGeoHash,
  setGpsPermission,
  incrementGpsRetryCount,
  resetGpsRetryCount,
  scheduleGpsRetry,
  setShowGpsBanner,
  setLoading,
  setError,
  clearLocation,
  resetLocationState
} = locationSlice.actions;

// Sélecteurs utiles
export const selectLocation = (state) => state.location.location;
export const selectStrategy = (state) => state.location.strategy;
export const selectGpsPermission = (state) => state.location.gpsPermission;
export const selectShowGpsBanner = (state) => state.location.showGpsBanner;
export const selectIsTemporary = (state) => state.location.strategy.isTemporary;
export const selectHasValidLocation = (state) =>
  state.location.location.coordinates.length === 2;

// Exporter le reducer
export default locationSlice.reducer;
